import { threedViewerConfig } from '../../config/colijn.config'

enum ColijnMethods {
    CreatePublicSession = 'createPublicSession',
    GetGoodIdFromArticleNr = 'getGoodIdFromArticleNr',
    ArticleQuickSel = 'articleQuickSel',
}

const publicServiceDomainURL: string = `${threedViewerConfig.url}publicservice/`

export async function has3DModel(ean: string): Promise<boolean> {
    try {
        const token = await getAuthenticationTokenForColijn()
        const goodId = await getGoodIdFromColijn(ean, token)
        return await checkIfColijnHasTheModel(goodId, token)
    } catch (e) {
        return false
    }
}

/**
 Doing requests for Colijn requires a dynamic token that expires after while
 */
function getAuthenticationTokenForColijn(): Promise<string> {
    const requestBody = JSON.stringify({
        method: ColijnMethods.CreatePublicSession,
        data: {
            upId: threedViewerConfig.schema,
        },
        token: '',
    })

    return fetch(
        `${publicServiceDomainURL}${ColijnMethods.CreatePublicSession}`,
        {
            method: 'POST',
            body: requestBody,
            headers: { 'Content-Type': 'application/json' },
        })
        .then((rawResponse) => rawResponse.json())
        .then((response) => response.data)
        .catch(() => Promise.reject())
}

/**
 We'd rather request the model url and look for a 404, but because it returns a CORS error we can't do that
 se we use this other url instead
 */
function getGoodIdFromColijn(ean: string, token: string): Promise<number> {
    const requestBody = JSON.stringify({
        method: ColijnMethods.GetGoodIdFromArticleNr,
        data: {
            parameterValues: {
                artikelnr: ean,
            },
        },
        token,
    })

    return fetch(
        `${publicServiceDomainURL}${ColijnMethods.GetGoodIdFromArticleNr}`,
        {
            method: 'POST',
            body: requestBody,
            headers: { 'Content-Type': 'application/json' },
        })
        .then((rawResponse) => rawResponse.json())
        .then((response) => response?.data?.resultObject)
        .catch(() => Promise.reject())
}

function checkIfColijnHasTheModel(goodId: number, token: string): Promise<boolean> {
    const requestBody = JSON.stringify({
        method: ColijnMethods.ArticleQuickSel,
        data: {
            parameterValues: {
                goodId,
            },
        },
        token,
    })

    return fetch(
        `${publicServiceDomainURL}${ColijnMethods.ArticleQuickSel}`,
        {
            method: 'POST',
            body: requestBody,
            headers: { 'Content-Type': 'application/json' },
        })
        .then((rawResponse) => rawResponse.json())
        .then((response) => Boolean(response.data?.resultObject?.oArticle[0]?.assetUrl))
        .catch(() => Promise.reject())
}

export function makeArUrl(sku: string): string {
    const isIOS = ! (window as any).MSStream && /iPad|iPhone|iPod/.test(navigator.userAgent)
    const fileExtension = isIOS ? 'usdz' : 'glb'
    const modelFileUrl = `${threedViewerConfig.threeDAssetPath}/UP_DBA/${sku}/${sku}.${fileExtension}`

    if (isIOS) {
        return `${modelFileUrl}#allowsContentScaling=0`
    }

    // eslint-disable-next-line max-len
    const intentAffix = '#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;end;'

    return `intent://arvr.google.com/scene-viewer/1.0?mode=ar_preferred&file=${encodeURIComponent(modelFileUrl)}${intentAffix}`
}

export function makeUrlForQrCode(): string {
    /*
     We tried putting the url for the model viewer in the QR code itself,
     but this url leads to a 404 in some android versions,
     so instead we redirect to the product page with a flag to trigger a custom logic to open the model
     */
    const density = 500
    const url = new URL(window.location.href)
    url.searchParams.append('open-ar-model', 'true')
    const viewerUrl = encodeURIComponent(url.href)

    return `https://quickchart.io/qr?text=${viewerUrl}&size=${density}&margin=0&ecLevel=H`
}
